import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getRnReevalNoteForm,
  updateRnReevalNoteForm,
} from "../../../../../services/patientService";
import {
  CommentsIcon,
  FileCheckIcon,
  HeartPulseIcon,
  LogOutIcon,
  MedicalNotesIcon,
  NurseIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  RN_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ASSESSMENT_AREA_OPTIONS,
  CHANGE_OPTIONS,
  CHANGES_SINCE_LAST_OPTIONS,
  GOAL_STATUS_OPTIONS,
  PLAN_OF_CARE_STATUS_OPTIONS,
  REEVAL_REASON_OPTIONS,
  YES_NO_OPTIONS,
  emptyAssessmentFindings,
  emptyGoals,
  emptyRnReevalNotePayload,
  toggleInList,
  today,
} from "./rnReevalNoteContent";
import {
  assertNurseVisitMetaForFlag,
  buildNurseVisitMetaProps,
  pickNurseVisitMeta,
} from "./nurseVisitCommon";
import NurseNotesCommonHeader from "./NurseNotesCommonHeader";
import WoundPresentSection from "./WoundPresentSection";
import {
  applyWoundAssessmentPayload,
  normalizeExclusiveYesNo,
} from "./woundAssessmentContent";
import {
  normalizeCommonHeaderState,
  resolveNurseNotesCommonHeader,
  withNormalizedCommonHeader,
} from "./nurseNotesCommonHeaderUtils";

const VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);

const ARRAY_KEYS = [
  "reevalReasons",
  "changesSinceLast",
  "planOfCareStatus",
];

const emptyValues = () => ({
  ...emptyRnReevalNotePayload(),
  visitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});


const RnReevalNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const normalizeLoadedPayload = useCallback((loadedPayload) => {
    const base = emptyRnReevalNotePayload();
    const merged = { ...base, ...loadedPayload };

    ARRAY_KEYS.forEach((key) => {
      merged[key] = Array.isArray(loadedPayload[key]) ? loadedPayload[key] : [];
    });

    const findings = Array.isArray(loadedPayload.assessmentFindings)
      ? loadedPayload.assessmentFindings
      : [];
    const byArea = Object.fromEntries(
      findings.map((row) => [row.area, row]),
    );
    merged.assessmentFindings = ASSESSMENT_AREA_OPTIONS.map((area) => ({
      area,
      lastAssessment: byArea[area]?.lastAssessment || "",
      currentAssessment: byArea[area]?.currentAssessment || "",
      change: byArea[area]?.change || "",
    }));

    const goals = Array.isArray(loadedPayload.goals) ? loadedPayload.goals : [];
    merged.goals =
      goals.length >= 3
        ? goals.slice(0, 3).map((row, idx) => ({
            id: row.id || `goal-${idx + 1}`,
            label: row.label || `Goal ${idx + 1}`,
            status: row.status || "",
            evidence: row.evidence || "",
            continueGoal: row.continueGoal || "",
          }))
        : emptyGoals();

    merged.commonHeader = normalizeCommonHeaderState(loadedPayload.commonHeader);

    applyWoundAssessmentPayload(merged, loadedPayload, {
      mrn: patient?.mrn,
    });

    merged.skilledNursingNeed = String(
      loadedPayload.skilledNursingNeed || "",
    ).trim();
    merged.planOfCareStatus = Array.isArray(loadedPayload.planOfCareStatus)
      ? loadedPayload.planOfCareStatus.map(String)
      : [];
    merged.planOfCareComments = String(
      loadedPayload.planOfCareComments || "",
    ).trim();
    merged.dischargeAnticipated = normalizeExclusiveYesNo(
      loadedPayload.dischargeAnticipated,
    );
    merged.dischargeExpectedDate = String(
      loadedPayload.dischargeExpectedDate || "",
    ).trim();
    merged.dischargeNeedsPlan = String(
      loadedPayload.dischargeNeedsPlan || "",
    ).trim();

    return merged;
  }, [patient?.mrn]);

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      setValues({
        ...normalizeLoadedPayload(loadedPayload),
        ...pickNurseVisitMeta(loadedPayload, data),
        visitDate: data.visitDate || loadedPayload.visitDate || today(),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      });
      resetSignatures();
    },
    [normalizeLoadedPayload, patientName, resetSignatures],
  );

  const buildPatientPrefill = useCallback(
    () => ({
      ...emptyRnReevalNotePayload(),
      mrn: patient?.mrn || "",
      assessmentFindings: emptyAssessmentFindings(),
      goals: emptyGoals(),
    }),
    [patient],
  );

  const mergePatientSnapshot = useCallback(
    (existingPayload) => {
      const prefill = buildPatientPrefill();
      return {
        ...prefill,
        ...existingPayload,
        mrn: existingPayload.mrn || prefill.mrn || "",
      };
    },
    [buildPatientPrefill],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveNurseNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Nurse Re-evaluation Note",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            visitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: { ...buildPatientPrefill(), commonHeader },
          });
          return;
        }

        const res = await getRnReevalNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveNurseNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...mergePatientSnapshot(existingPayload), commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const updateAssessmentRow = (area, patch) => {
    setValues((prev) => ({
      ...prev,
      assessmentFindings: prev.assessmentFindings.map((row) =>
        row.area === area ? { ...row, ...patch } : row,
      ),
    }));
  };

  const updateGoalRow = (goalId, patch) => {
    setValues((prev) => ({
      ...prev,
      goals: prev.goals.map((row) =>
        row.id === goalId ? { ...row, ...patch } : row,
      ),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        RN_FORM_EDITOR_ROLES,
        "View only — only RN/LVN can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      visitDate,
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      ...rnFields
    } = values;

    const nestedPayload = withNormalizedCommonHeader({
      ...rnFields,
      dateOfBirth: values.dateOfBirth ? formatDateForInput(values.dateOfBirth) : "",
      visitDate: visitDate ? formatDateForInput(visitDate) : "",
      assessmentFindings: (values.assessmentFindings || []).map((row) => ({
        area: row.area,
        lastAssessment: row.lastAssessment || "",
        currentAssessment: row.currentAssessment || "",
        change: row.change || "",
      })),
      goals: (values.goals || []).map((row) => ({
        id: row.id,
        label: row.label || "",
        status: row.status || "",
        evidence: row.evidence || "",
        continueGoal: row.continueGoal || "",
      })),
      commonHeader: values.commonHeader,
    });

    ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      visitDate: visitDate ? formatDateForInput(visitDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertNurseVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateRnReevalNoteForm,
        filledByUserId,
        scheduleDate: values.visitDate
          ? formatDateForInput(values.visitDate)
          : null,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Nurse Re-evaluation Note
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Re-evaluation visit documentation for skilled nursing home health
            services.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn || values.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              values.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          dateLabel="Date"
          dateValue={displayDateUS(values.visitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Date
                </div>
                <InputDatePicker
                  name="visitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.visitDate) || ""}
                  onChange={(event) => setValue("visitDate", event.target.value)}
                  max={VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildNurseVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <NurseNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <Section icon={MedicalNotesIcon} title="7. Re-evaluation Information">
          <SubHeading>Reason for Re-Evaluation (Check all that apply)</SubHeading>
          <CheckboxGrid
            options={REEVAL_REASON_OPTIONS}
            values={values.reevalReasons}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("reevalReasons", opt)}
            columns={4}
          />
        </Section>

        <Section icon={FileCheckIcon} title="8. Changes Since Last Assessment">
          <CheckboxGrid
            options={CHANGES_SINCE_LAST_OPTIONS}
            values={values.changesSinceLast}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("changesSinceLast", opt)}
            columns={3}
          />
        </Section>

        <Section icon={HeartPulseIcon} title="9. Current Assessment Findings">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full text-left text-[11px]">
              <thead>
                <tr className="bg-slate-50 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-2.5 py-2 w-[22%]">Assessment Area</th>
                  <th className="px-2.5 py-2">Last Assessment</th>
                  <th className="px-2.5 py-2">Current Assessment</th>
                  <th className="px-2.5 py-2 w-[8rem]">Change</th>
                </tr>
              </thead>
              <tbody>
                {(values.assessmentFindings || []).map((row) => (
                  <tr key={row.area} className="border-t border-slate-100">
                    <td className="px-2.5 py-1.5 font-medium text-slate-700 align-middle">
                      {row.area}
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.lastAssessment}
                        disabled={!canEdit}
                        onChange={(e) =>
                          updateAssessmentRow(row.area, {
                            lastAssessment: e.target.value,
                          })
                        }
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.currentAssessment}
                        disabled={!canEdit}
                        onChange={(e) =>
                          updateAssessmentRow(row.area, {
                            currentAssessment: e.target.value,
                          })
                        }
                      />
                    </td>
                    <td className="px-2 py-1">
                      {printMode || !canEdit ? (
                        <div className={`${fieldClass} flex items-center`}>
                          {row.change || "—"}
                        </div>
                      ) : (
                        <select
                          className={fieldClass}
                          value={row.change || ""}
                          onChange={(e) =>
                            updateAssessmentRow(row.area, {
                              change: e.target.value,
                            })
                          }
                        >
                          {CHANGE_OPTIONS.map((opt) => (
                            <option key={opt} value={opt === "-" ? "" : opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section icon={NurseIcon} title="Wound Assessment (If Applicable)">
          <WoundPresentSection
            values={values}
            canEdit={canEdit}
            printMode={printMode}
            onChange={(key, next) => setValue(key, next)}
            onToggleList={(key, opt) => toggleListValue(key, opt)}
          />
        </Section>

        <Section icon={TargetIcon} title="10. Progress Toward Goals">
          <p className="text-[11px] text-slate-500 italic mb-2">
            Indicate progress toward goals established in the Plan of Care.
          </p>
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full text-left text-[11px]">
              <thead>
                <tr className="bg-slate-50 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-2.5 py-2 w-[5.5rem]">Goal</th>
                  <th className="px-2.5 py-2">Status</th>
                  <th className="px-2.5 py-2">Evidence / Measurement</th>
                  <th className="px-2.5 py-2 w-[7.5rem]">Continue?</th>
                </tr>
              </thead>
              <tbody>
                {(values.goals || []).map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-2.5 py-2 font-medium text-slate-700 align-middle">
                      {row.label}
                    </td>
                    <td className="px-2 py-2 align-middle">
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {GOAL_STATUS_OPTIONS.map((opt) => (
                          <label
                            key={opt}
                            className={`inline-flex items-center gap-1.5 text-[11px] text-slate-700 ${
                              canEdit ? "cursor-pointer" : "cursor-default"
                            }`}
                          >
                            <input
                              type="radio"
                              name={`${row.id}-status`}
                              className="h-3.5 w-3.5 border-slate-300 text-indigo-500"
                              checked={row.status === opt}
                              disabled={!canEdit}
                              onChange={() =>
                                updateGoalRow(row.id, { status: opt })
                              }
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.evidence}
                        disabled={!canEdit}
                        onChange={(e) =>
                          updateGoalRow(row.id, { evidence: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-2 py-2 align-middle">
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {YES_NO_OPTIONS.map((opt) => (
                          <label
                            key={opt}
                            className={`inline-flex items-center gap-1.5 text-[11px] text-slate-700 ${
                              canEdit ? "cursor-pointer" : "cursor-default"
                            }`}
                          >
                            <input
                              type="radio"
                              name={`${row.id}-continue`}
                              className="h-3.5 w-3.5 border-slate-300 text-indigo-500"
                              checked={row.continueGoal === opt}
                              disabled={!canEdit}
                              onChange={() =>
                                updateGoalRow(row.id, { continueGoal: opt })
                              }
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section
          icon={NurseIcon}
          title="11. Skilled Nursing Need / Medical Necessity"
        >
          <Field label="Patient continues to require skilled nursing care for">
            <textarea
              rows={3}
              className={textareaClass}
              value={values.skilledNursingNeed || ""}
              disabled={!canEdit}
              placeholder="Document ongoing skilled nursing need and medical necessity…"
              onChange={(e) => setValue("skilledNursingNeed", e.target.value)}
            />
          </Field>
        </Section>

        <Section icon={CommentsIcon} title="12. Plan of Care / Orders">
          <div>
            <SubHeading>Plan of Care Status</SubHeading>
            <CheckboxGrid
              options={PLAN_OF_CARE_STATUS_OPTIONS}
              values={values.planOfCareStatus}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("planOfCareStatus", opt)}
              columns={3}
            />
          </div>
          <div className="mt-3">
            <Field label="Comments / New Orders">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.planOfCareComments || ""}
                disabled={!canEdit}
                placeholder="Describe plan changes, new orders, or frequency adjustments…"
                onChange={(e) => setValue("planOfCareComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={LogOutIcon} title="13. Discharge Planning">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
            <div>
              <SubHeading>Discharge Anticipated?</SubHeading>
              <ExclusiveChoiceGrid
                name="dischargeAnticipated"
                options={YES_NO_OPTIONS}
                value={values.dischargeAnticipated || ""}
                canEdit={canEdit}
                onChange={(opt) => {
                  setValue("dischargeAnticipated", opt);
                  if (opt !== "Yes") setValue("dischargeExpectedDate", "");
                }}
                columns={2}
              />
            </div>
            {values.dischargeAnticipated === "Yes" ? (
              <Field label="If Yes, Expected Date">
                {printMode ? (
                  <div className={`${fieldClass} flex items-center`}>
                    {displayDateUS(values.dischargeExpectedDate) || "—"}
                  </div>
                ) : (
                  <InputDatePicker
                    name="dischargeExpectedDate"
                    formatSeparator="/"
                    allowManualEntry
                    disabled={!canEdit}
                    value={
                      formatDateForInput(values.dischargeExpectedDate) || ""
                    }
                    onChange={(event) =>
                      setValue("dischargeExpectedDate", event.target.value)
                    }
                    className="w-full"
                    inputClassName={`${fieldClass} bg-white`}
                  />
                )}
              </Field>
            ) : null}
          </div>
          <div className="mt-3">
            <Field label="Discharge Needs / Plan">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.dischargeNeedsPlan || ""}
                disabled={!canEdit}
                placeholder="Document anticipated discharge needs and plan…"
                onChange={(e) => setValue("dischargeNeedsPlan", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Nurse Authentication"
          patientSectionTitle="Patient / Caregiver (Acknowledgment of Visit)"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

RnReevalNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default RnReevalNoteForm;
